import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as companyService from '../services/companyService';
import * as userService from '../services/userService';
import { v4 as uuidv4 } from 'uuid';

// Types
type WizardStep = 1 | 2 | 3 | 4 | 5;

interface CompanyWizardFormData {
  name: string;
  subdomain: string;
  website: string;
  about: string;
  industry: string;
  company_size: string;
  phone: string;
  billing_email: string;
  primary_contact_name: string;
  primary_contact_phone: string;
  primary_contact_email: string;
  secondary_contact_name: string;
  secondary_contact_phone: string;
  secondary_contact_email: string;
  billing_contact_name: string;
  billing_contact_phone: string;
  billing_contact_email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

interface PendingInvitation {
  tempId: string;
  email: string;
  role: 'project_manager' | 'contributor';
}

const STORAGE_KEY = 'company_setup_wizard_data';
const INVITATIONS_KEY = 'company_setup_wizard_invitations';

export default function CompanySetupWizard() {
  const { user, company, token } = useAuth();
  const navigate = useNavigate();

  // State management
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formData, setFormData] = useState<CompanyWizardFormData>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved wizard data:', e);
      }
    }
    return {
      name: company?.name || '',
      subdomain: '',
      website: '',
      about: '',
      industry: '',
      company_size: '',
      phone: '',
      billing_email: user?.email || '',
      primary_contact_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
      primary_contact_phone: '',
      primary_contact_email: user?.email || '',
      secondary_contact_name: '',
      secondary_contact_phone: '',
      secondary_contact_email: '',
      billing_contact_name: '',
      billing_contact_phone: '',
      billing_contact_email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'USA'
    };
  });

  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>(() => {
    const saved = localStorage.getItem(INVITATIONS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved invitations:', e);
      }
    }
    return [];
  });

  const [invitationInput, setInvitationInput] = useState({ email: '', role: 'contributor' as 'project_manager' | 'contributor' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  // Save form data to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Save invitations to localStorage
  useEffect(() => {
    localStorage.setItem(INVITATIONS_KEY, JSON.stringify(pendingInvitations));
  }, [pendingInvitations]);

  // Clear localStorage on successful completion
  const clearWizardData = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(INVITATIONS_KEY);
  };

  // Handle form field changes
  const handleInputChange = (field: keyof CompanyWizardFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  // Check subdomain availability
  const checkSubdomain = async (subdomain: string) => {
    if (!subdomain || !token) return;

    setCheckingSubdomain(true);
    try {
      const result = await companyService.checkSubdomainAvailability(subdomain, token);
      setSubdomainAvailable(result.available);
      if (!result.available) {
        setErrors(prev => ({ ...prev, subdomain: 'This subdomain is already taken' }));
      }
    } catch (error: any) {
      console.error('Error checking subdomain:', error);
      setErrors(prev => ({ ...prev, subdomain: 'Failed to check subdomain availability' }));
    } finally {
      setCheckingSubdomain(false);
    }
  };

  // Add invitation
  const handleAddInvitation = async () => {
    const { email, role } = invitationInput;

    // Validate email
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors(prev => ({ ...prev, invitation_email: 'Invalid email address' }));
      return;
    }

    // Check if already in pending list
    if (pendingInvitations.some(inv => inv.email.toLowerCase() === email.toLowerCase())) {
      setErrors(prev => ({ ...prev, invitation_email: 'This email is already in the invitation list' }));
      return;
    }

    // Check if email exists in system
    if (token) {
      try {
        const result = await userService.checkEmailExists(email, token);
        if (result.exists) {
          setErrors(prev => ({ ...prev, invitation_email: 'User with this email already exists' }));
          return;
        }
      } catch (error) {
        console.error('Error checking email:', error);
      }
    }

    // Add to pending list
    setPendingInvitations(prev => [...prev, { tempId: uuidv4(), email, role }]);
    setInvitationInput({ email: '', role: 'contributor' });
    setErrors(prev => {
      const updated = { ...prev };
      delete updated.invitation_email;
      return updated;
    });
  };

  // Remove invitation
  const handleRemoveInvitation = (tempId: string) => {
    setPendingInvitations(prev => prev.filter(inv => inv.tempId !== tempId));
  };

  // Navigate to next step
  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((currentStep + 1) as WizardStep);
      window.scrollTo(0, 0);
    }
  };

  // Navigate to previous step
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as WizardStep);
      window.scrollTo(0, 0);
    }
  };

  // Skip wizard
  const handleSkip = () => {
    if (window.confirm('Are you sure you want to skip the setup wizard? You can complete this later in Company Settings.')) {
      clearWizardData();
      navigate('/dashboard');
    }
  };

  // Submit wizard
  const handleSubmit = async () => {
    if (!user || !token) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      // Update company profile
      const updateData: any = {};

      // Add all non-empty fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          updateData[key] = value;
        }
      });

      // Mark setup as completed
      updateData.setup_completed = true;

      await companyService.updateCompany(user.company_id, updateData, token);

      // Send invitations if any
      if (pendingInvitations.length > 0) {
        try {
          const invitations = pendingInvitations.map(inv => ({
            email: inv.email,
            role: inv.role
          }));
          await userService.inviteBulk(invitations, token);
        } catch (error) {
          console.error('Error sending invitations:', error);
          // Don't fail the wizard if invitations fail
        }
      }

      // Clear wizard data
      clearWizardData();

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error submitting wizard:', error);
      setErrors({ submit: error.message || 'Failed to complete setup' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Step 1: Basic Information
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Basic Information</h2>
        <p className="text-gray-600">Tell us about your company</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            value={formData.name}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
            placeholder="Enter company name"
          />
          <p className="mt-1 text-sm text-gray-500">Company name was set during registration</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="https://www.example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            About
          </label>
          <textarea
            value={formData.about}
            onChange={(e) => handleInputChange('about', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Tell us about your company..."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g., Technology, Healthcare"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Size
            </label>
            <select
              value={formData.company_size}
              onChange={(e) => handleInputChange('company_size', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-500">201-500 employees</option>
              <option value="501-1000">501-1000 employees</option>
              <option value="1000+">1000+ employees</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 2: Contact Information
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Information</h2>
        <p className="text-gray-600">Primary and secondary contact details</p>
      </div>

      <div className="space-y-6">
        {/* Primary Contact */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Primary Contact</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.primary_contact_name}
                onChange={(e) => handleInputChange('primary_contact_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="John Doe"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.primary_contact_phone}
                  onChange={(e) => handleInputChange('primary_contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.primary_contact_email}
                  onChange={(e) => handleInputChange('primary_contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Contact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Secondary Contact (Optional)</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.secondary_contact_name}
                onChange={(e) => handleInputChange('secondary_contact_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Jane Smith"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.secondary_contact_phone}
                  onChange={(e) => handleInputChange('secondary_contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+1 (555) 987-6543"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.secondary_contact_email}
                  onChange={(e) => handleInputChange('secondary_contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="jane@example.com"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Billing Contact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Contact (Optional)</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.billing_contact_name}
                onChange={(e) => handleInputChange('billing_contact_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Billing Department"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.billing_contact_phone}
                  onChange={(e) => handleInputChange('billing_contact_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+1 (555) 111-2222"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.billing_contact_email}
                  onChange={(e) => handleInputChange('billing_contact_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="billing@example.com"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 3: Address
  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Address</h2>
        <p className="text-gray-600">Where is your company located?</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 1
          </label>
          <input
            type="text"
            value={formData.address_line1}
            onChange={(e) => handleInputChange('address_line1', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="123 Main Street"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address Line 2 (Optional)
          </label>
          <input
            type="text"
            value={formData.address_line2}
            onChange={(e) => handleInputChange('address_line2', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Suite 100"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="San Francisco"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State/Province
            </label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="CA"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postal Code
            </label>
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="94102"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="USA"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render Step 4: Team Invitations
  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite Team Members</h2>
        <p className="text-gray-600">Add users to your company (optional)</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={invitationInput.email}
                onChange={(e) => {
                  setInvitationInput(prev => ({ ...prev, email: e.target.value }));
                  if (errors.invitation_email) {
                    setErrors(prev => {
                      const updated = { ...prev };
                      delete updated.invitation_email;
                      return updated;
                    });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="user@example.com"
              />
              {errors.invitation_email && <p className="mt-1 text-sm text-red-600">{errors.invitation_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={invitationInput.role}
                onChange={(e) => setInvitationInput(prev => ({ ...prev, role: e.target.value as 'project_manager' | 'contributor' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="contributor">Contributor</option>
                <option value="project_manager">Project Manager</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleAddInvitation}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Invitation
          </button>
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Pending Invitations ({pendingInvitations.length})
          </h3>
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => (
              <div
                key={invitation.tempId}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                  <p className="text-xs text-gray-500">
                    {invitation.role === 'project_manager' ? 'Project Manager' : 'Contributor'}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveInvitation(invitation.tempId);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {pendingInvitations.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No invitations added yet. You can add team members later.</p>
        </div>
      )}
    </div>
  );

  // Render Step 5: Review and Complete
  const renderStep5 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Complete</h2>
        <p className="text-gray-600">Review your information before completing setup</p>
      </div>

      {errors.submit && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Company Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.name || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subdomain</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.subdomain ? `${formData.subdomain}.nivoon.com` : '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Website</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.website || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Industry</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.industry || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Company Size</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.company_size || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.phone || '-'}</dd>
            </div>
          </dl>
          {formData.about && (
            <div className="mt-4">
              <dt className="text-sm font-medium text-gray-500">About</dt>
              <dd className="mt-1 text-sm text-gray-900">{formData.about}</dd>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
          <div className="space-y-4">
            {formData.primary_contact_name && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Primary Contact</h4>
                <dl className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Name</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.primary_contact_name}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.primary_contact_email || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formData.primary_contact_phone || '-'}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* Address */}
        {(formData.address_line1 || formData.city) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Address</h3>
            <dl className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {formData.address_line1 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address Line 1</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.address_line1}</dd>
                </div>
              )}
              {formData.address_line2 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address Line 2</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.address_line2}</dd>
                </div>
              )}
              {formData.city && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">City</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.city}</dd>
                </div>
              )}
              {formData.state && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">State</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.state}</dd>
                </div>
              )}
              {formData.postal_code && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Postal Code</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.postal_code}</dd>
                </div>
              )}
              {formData.country && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Country</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formData.country}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* Team Invitations */}
        {pendingInvitations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Team Invitations ({pendingInvitations.length})
            </h3>
            <div className="space-y-2">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.tempId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                    <p className="text-xs text-gray-500">
                      {invitation.role === 'project_manager' ? 'Project Manager' : 'Contributor'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render progress indicator
  const renderProgress = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {[1, 2, 3, 4, 5].map((step) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step === currentStep
                    ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                    : step < currentStep
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {step < currentStep ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <p className="mt-2 text-xs text-gray-600 text-center">
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Contacts'}
                {step === 3 && 'Address'}
                {step === 4 && 'Team'}
                {step === 5 && 'Review'}
              </p>
            </div>
            {step < 5 && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">Company Setup Wizard</h1>
              <p className="text-blue-100 mt-1">Complete your company profile</p>
            </div>
            {currentStep === 1 && (
              <button
                onClick={handleSkip}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-sm font-medium rounded-full text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        {renderProgress()}

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-sm font-medium rounded-full transition-colors ${
              currentStep === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {currentStep < 5 ? (
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 px-6 py-3 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-6 py-3 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Completing...' : 'Complete Setup'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
